import {Action, Card, useDialog} from 'platform/components';
import {Box} from 'platform/foundation';

import {useDispatch} from 'react-redux';

import {isNotNil, mergeAll} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {BulkContextItem, documentContextApi} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {useGetBusinessCaseQuery} from '@dms/api/sales';
import {
  DOCUMENT_ACTION_COLUMNS,
  useGetDocumentDatagridActions,
} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {
  DmsDataGrid,
  type DmsDataGridProps,
  Main,
  refreshDmsDatagrid,
  UploadDocumentsDialog,
} from '@dms/shared';

import {buildArray, useRequiredParams} from 'shared/web';

import type {QueryFilterObject} from 'features/eag-grid';

import {DOCUMENT_DATA_GRID_CODES} from './constants/DOCUMENT_DATA_GRID_CODES';
import {getPurchaseVehicleIdFromBusinessCase} from './utils/getPurchaseVehicleIdFromBusinessCase';
import {getSaleVehicleIdFromBusinessCase} from './utils/getSaleVehicleIdFromBusinessCase';

export function BusinessCaseDocuments(props: Pick<DmsDataGridProps, 'maximization'>) {
  const {id: businessCaseId} = useRequiredParams();
  const {data: businessCase} = useGetBusinessCaseQuery({businessCaseId});

  const {data: customer} = useGetCustomerV2Query(
    {customerId: businessCase?.customerId ?? ''},
    {skip: isNilOrEmpty(businessCase?.customerId ?? '')}
  );
  const [isUploadDialogOpen, openUploadDialog, closeUploadDialog] = useDialog();

  const {id} = useRequiredParams();

  const dispatch = useDispatch();

  const refreshDataGrid = () => refreshDmsDatagrid(DOCUMENT_DATA_GRID_CODES.BUSINESS_CASE);

  const onDocumentCreated = () => {
    refreshDataGrid();

    dispatch(documentContextApi.util.invalidateTags([{type: 'documentsCount', id}]));
  };

  const queryModifier = (filter: QueryFilterObject) => mergeAll([filter, {businessCaseId}]);

  const saleVehicleId = getSaleVehicleIdFromBusinessCase(businessCase);
  const purchaseVehicleId = getPurchaseVehicleIdFromBusinessCase(businessCase);

  const [actions, bulkSignatureAction] = useGetDocumentDatagridActions({
    refreshData: refreshDataGrid,
    customerId: customer?.id,
    contextTarget: 'business-case',
    contextId: businessCaseId,
    vehicleId: saleVehicleId ?? purchaseVehicleId,
  });

  return (
    <Main>
      <Card
        title={i18n.t('page.businessCase.documents.title')}
        actions={buildArray<Action>([
          {
            type: 'button',
            variant: 'link',
            leftIcon: 'action/backup',
            title: i18n.t('entity.document.actions.uploadDocument'),
            onClick: openUploadDialog,
          },
        ]).add(...bulkSignatureAction)}
      >
        <Box height={130} minHeight={50} position="relative">
          <DmsDataGrid
            maximization={props.maximization}
            data-testid="businessCaseDocumentsDataGrid"
            gridCode={DOCUMENT_DATA_GRID_CODES.BUSINESS_CASE}
            emptyState={{
              headline: i18n.t('page.businessCase.notifications.noDocuments'),
              subheadline: i18n.t('page.businessCase.notifications.documentListWillAppearHere'),
            }}
            actionCallback={actions}
            predefinedColumns={DOCUMENT_ACTION_COLUMNS}
            queryModifier={queryModifier}
            hasSilentRefetching
            actionsAlignment="left"
          />
        </Box>
      </Card>
      <UploadDocumentsDialog
        isOpen={isUploadDialogOpen}
        handleClose={closeUploadDialog}
        onDocumentCreated={onDocumentCreated}
        contextTarget="business-case"
        contextId={businessCaseId}
        bulkContext={buildArray<BulkContextItem>([
          {
            target: 'business-case',
            targetId: businessCaseId,
          },
        ]).when(isNotNil(saleVehicleId) || isNotNil(purchaseVehicleId), {
          target: 'vehicle',
          targetId: saleVehicleId ?? purchaseVehicleId ?? '',
        })}
      />
    </Main>
  );
}
